import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";

import agent from "../../utils/axios";
import { hasAnyRole, isAdmin } from "../../utils/auth";
import { formatMoney } from "../../utils/format";
import { Payment1099 } from "../../types/payment1099";
import { PaymentDetail } from "../../types/paymentDetail";
import { pdfavailable } from "../../types/pdfavailable";
import { formatMailingAddress } from "../../utils/address";
import { apiErrorMessage } from "../../utils/apiError";
import { saveBlob } from "../../utils/download";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useTaxIdReveal } from "../../hooks/useTaxIdReveal";
import {
  Callout,
  DetailsExportCluster,
  FilterBar,
  MaskedTaxId,
  PageHeader,
  PayeeCell,
  Pdf1099Button,
  RevealAllButton,
  SearchField,
  useToast,
} from "../../components/ui";

const SEARCH_DEBOUNCE_MS = 300;

/** Payee page for a row, opened on its paid bills (today's drill-down). */
const payeeLink = (id: string) => `/payees/detail/${id}?Status=Paid`;

/**
 * Downloads the 1099 PDF. The WHOLE row object, exactly as the API returned it, is posted
 * back: the server builds the PDF from it.
 */
function handlePdfDownload(
  row: Payment1099,
  onError: (message: string) => void
): void {
  agent.Payments.download1099PDF(row)
    .then((response: Blob) => {
      saveBlob(response, `${row.payeeName}_${row.year}_1099.pdf`);
    })
    .catch((error: unknown) => {
      onError(
        apiErrorMessage(
          error,
          `Couldn't download the 1099 for ${row.payeeName || "this payee"}.`
        )
      );
    });
}

function TaxIdHeader({
  canReveal,
  revealAll,
  onToggleAll,
}: {
  canReveal: boolean;
  revealAll: boolean;
  onToggleAll: () => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <span className="MuiDataGrid-columnHeaderTitle">Tax ID</span>
      {canReveal && (
        <RevealAllButton revealAll={revealAll} onToggle={onToggleAll} />
      )}
    </Box>
  );
}

function PaymentsOverlay({
  error,
  year,
  onRetry,
}: {
  error: boolean;
  year: number;
  onRetry: () => void;
}) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 6,
      }}
    >
      {/* AB#6106: distinguish "the request failed" from "this year genuinely has no
          payments". Rendering the same empty-state for both is what let the original
          failure sit unnoticed in Test and Production. */}
      {error ? (
        <Callout
          tone="err"
          role="alert"
          title={`Could not load payments for tax year ${year}.`}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Please retry or contact support if this continues.
        </Callout>
      ) : (
        <Typography color="text.secondary">
          No payments available for Tax year ending {year}
        </Typography>
      )}
    </Box>
  );
}

/** The 1099 visibility switch as a setting row. */
function SettingRow({
  year,
  checked,
  onChange,
}: {
  year: number;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const titleId = "pdf-visible-title";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        px: 4,
        py: 3,
      }}
    >
      <VisibilityOutlined sx={{ color: "text.secondary" }} aria-hidden />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id={titleId} sx={{ fontWeight: 500 }}>
          1099s visible for {year}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Turns 1099 downloads on for this tax year.
        </Typography>
      </Box>
      <Switch
        checked={checked}
        onChange={onChange}
        inputProps={{
          "aria-label": `1099s visible for ${year}`,
          "aria-describedby": titleId,
        }}
      />
    </Box>
  );
}

const currentYear = new Date().getFullYear();
function Payments() {
  const toast = useToast();
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState<number[]>([]);
  const [yearsError, setYearsError] = useState(false);
  const [paymentsError, setPaymentsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [order, setOrder] = useState<"desc" | "asc">("asc");
  const [orderBy, setOrderBy] = useState("payeeId");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<Payment1099[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const searchTerm = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const [checked, setChecked] = React.useState(false);
  const [paymentCountFilter, setPaymentCountFilter] = React.useState("all");
  const [clientCountFilter, setClientCountFilter] = React.useState("all");

  // Downloads, the 1099 column and the visibility switch: Developer or Admin (unchanged).
  const canDownload = useMemo(() => hasAnyRole(["Developer", "Admin"]), []);
  // D1: only Admin may reveal a tax ID.
  const canReveal = useMemo(() => isAdmin(), []);
  const reveal = useTaxIdReveal();
  const requestSeq = useRef(0);
  const lastSearch = useRef(searchTerm);

  const handlePaymentCountFilterChange = (event: SelectChangeEvent) => {
    setPaymentCountFilter(event.target.value);
  };

  const handleClientCountFilterChange = (event: SelectChangeEvent) => {
    setClientCountFilter(event.target.value);
  };

  const notifyError = useCallback(
    (message: string) => toast.error(message),
    [toast]
  );

  const handleChangePDF = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pdfData: pdfavailable = {
      year: year,
      available: event.target.checked,
    };
    setChecked(event.target.checked);
    agent.Options.updateTaxPdfOption(pdfData).catch((error: unknown) => {
      setChecked(!pdfData.available);
      toast.error(
        apiErrorMessage(
          error,
          `Couldn't change whether 1099s are visible for ${pdfData.year}.`
        )
      );
    });
  };

  const fetchDataAndDownloadCSV = async () => {
    setLoadingButton(true);
    try {
      const { data } = await agent.Payments.getAllPaymentsByYear(
        year.toString()
      );
      const sanitizedData = (data ?? []).map(
        ({ paymentStatus, pdfVisible, ...rest }: any) => rest
      );
      if (sanitizedData.length === 0) {
        toast.info(`There are no 1099 payments to download for ${year}.`);
        return;
      }
      const headers = Object.keys(sanitizedData[0]).join(",");

      const rows = sanitizedData.map((row: any) =>
        Object.values(row)
          .map((value) => `"${value}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      saveBlob(
        new Blob([csvContent], { type: "text/csv" }),
        `1099Payees${year}.csv`
      );
    } catch (error) {
      toast.error(
        apiErrorMessage(error, "Couldn't download the 1099 summary.")
      );
    } finally {
      setLoadingButton(false);
    }
  };

  const fetchDataAndDownloadDetailCSV = async () => {
    setLoadingButton(true);
    try {
      const detailedData: PaymentDetail[] =
        await agent.Payments.getAllPaymentDetailsByYear(
          year.toString(),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          paymentCountFilter,
          clientCountFilter
        );

      const paymentSuffix =
        paymentCountFilter === "multiple"
          ? "_MultiplePayments"
          : paymentCountFilter === "single"
          ? "_SinglePayment"
          : "";

      const clientSuffix =
        clientCountFilter === "compassAndOther" ? "_CompassAndOther" : "";

      const filterSuffix = paymentSuffix + clientSuffix;

      const headers =
        "Payee ID,Name,Address,Amount,Tax ID,Program Name,Taxable,Payment Date,Requesting Client";
      const rows = detailedData.map((row: PaymentDetail) =>
        [
          row.payeeId,
          row.payeeName,
          `${row.address1}${row.address2 ? `, ${row.address2}` : ""}, ${
            row.city
          }, ${row.state} ${row.postalCode}`,
          row.amount,
          row.taxId,
          row.programName,
          row.taxable ? "Yes" : "No",
          new Date(row.paymentDate).toLocaleDateString(),
          row.requestingClient,
        ]
          .map((value) => `"${value}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      saveBlob(
        new Blob([csvContent], { type: "text/csv" }),
        `1099PayeesDetail${year}${filterSuffix}.csv`
      );
    } catch (error) {
      toast.error(
        apiErrorMessage(error, "Couldn't download the payment details.")
      );
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    async function getYears() {
      try {
        const response = await agent.Payments.getTaxYear();
        setYears(response ?? []);
        setYearsError(false);
      } catch {
        // AB#6106: this used to be `finally {}` with no catch, so a failed /Tax/taxYears call became
        // an unhandled rejection — setYears never ran and the Year dropdown rendered empty with no
        // indication anything had gone wrong. Surface it instead of failing silently.
        setYears([]);
        setYearsError(true);
      }
    }

    getYears();
  }, []);

  useEffect(() => {
    // A new search starts from the first page (fetch once, from page 0).
    if (lastSearch.current !== searchTerm) {
      lastSearch.current = searchTerm;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    const seq = ++requestSeq.current;
    setLoading(true);

    async function getPayments() {
      try {
        const response = await agent.Payments.getAllPaymentsByYear(
          year.toString(),
          page,
          rowsPerPage,
          orderBy,
          order,
          searchTerm
        );
        if (seq !== requestSeq.current) return;
        setData(response?.data ?? []);
        setTotalCount(response?.count ?? 0);
        const response2 = await agent.Options.getTaxPdfOptionByYear(
          year.toString()
        );
        if (seq !== requestSeq.current) return;
        setChecked(response2);
        setPaymentsError(false);
      } catch {
        // AB#6106: same missing-catch pattern as getYears — a failed request left the grid on stale
        // data with no error surfaced. Track the failure separately so the grid can say "couldn't
        // load" rather than "no payments", which is the ambiguity that made this bug undiagnosable.
        if (seq !== requestSeq.current) return;
        setData([]);
        setTotalCount(0);
        setPaymentsError(true);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }

    getPayments();
  }, [page, rowsPerPage, searchTerm, year, orderBy, order, reloadKey]);

  const handleChange = (event: SelectChangeEvent) => {
    setYear(event.target.value as unknown as number);
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setOrderBy(model[0].field);
      setOrder(model[0].sort as "asc" | "desc");
    }
  };

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  const { revealAll, toggleAll, toggleRow, isRevealed } = reveal;

  const columns = useMemo<GridColDef<Payment1099>[]>(() => {
    const cols: GridColDef<Payment1099>[] = [
      {
        // Hidden by default (the id shows under the name); kept so it can still be
        // shown, sorted and filtered from the column menu as before.
        field: "id",
        headerName: "Payee ID",
        width: 220,
        renderCell: (params: GridRenderCellParams<Payment1099>) => (
          <Link component={RouterLink} to={payeeLink(String(params.value))}>
            {String(params.value)}
          </Link>
        ),
      },
      {
        field: "payeeName",
        headerName: "Payee",
        flex: 1,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<Payment1099>) => (
          <PayeeCell
            name={params.row.payeeName}
            id={params.row.id}
            to={payeeLink(params.row.id)}
          />
        ),
      },
      {
        field: "taxId",
        headerName: "Tax ID",
        width: 200,
        renderHeader: () => (
          <TaxIdHeader
            canReveal={canReveal}
            revealAll={revealAll}
            onToggleAll={toggleAll}
          />
        ),
        renderCell: (params: GridRenderCellParams<Payment1099>) => (
          <MaskedTaxId
            value={params.row.taxId}
            canReveal={canReveal}
            revealed={canReveal ? isRevealed(params.row.id) : false}
            onToggle={() => toggleRow(params.row.id)}
          />
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params: GridValueFormatterParams<number>) =>
          formatMoney(params.value),
        renderCell: (params: GridRenderCellParams<Payment1099>) => (
          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {params.formattedValue}
          </Box>
        ),
      },
    ];

    // 1099 column before Address, only for those who can download.
    if (canDownload) {
      cols.push({
        field: "actions",
        headerName: "1099",
        width: 110,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Payment1099>) => (
          <Pdf1099Button
            payeeName={params.row.payeeName}
            disabled={params.row.pdfVisible === false}
            onClick={() => handlePdfDownload(params.row, notifyError)}
          />
        ),
      });
    }

    // Address column last
    cols.push({
      field: "address",
      headerName: "Address",
      flex: 1.2,
      minWidth: 260,
      valueGetter: (params: GridValueGetterParams<Payment1099>) =>
        formatMailingAddress(params.row),
      renderCell: (params: GridRenderCellParams<Payment1099>) => (
        <Box
          component="span"
          title={String(params.value ?? "")}
          sx={{
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {String(params.value ?? "")}
        </Box>
      ),
    });

    return cols;
  }, [
    canDownload,
    canReveal,
    revealAll,
    toggleAll,
    toggleRow,
    isRevealed,
    notifyError,
  ]);

  const NoRowsOverlay = useMemo(
    () =>
      function NoPaymentsOverlay() {
        return (
          <PaymentsOverlay error={paymentsError} year={year} onRetry={retry} />
        );
      },
    [paymentsError, year, retry]
  );

  const yearSelect = (
    <FormControl size="small" sx={{ minWidth: 120 }} error={yearsError}>
      <InputLabel id="year-select-label">Year</InputLabel>
      <Select
        labelId="year-select-label"
        id="year-select"
        value={years.includes(year) ? year.toString() : ""}
        label="Year"
        onChange={handleChange}
      >
        {years.map((yearOption) => (
          <MenuItem key={yearOption} value={yearOption}>
            {yearOption}
          </MenuItem>
        ))}
      </Select>
      {/* AB#6106: an empty dropdown used to be indistinguishable from a failed request.
          Say which one it is so the next report is diagnosable. */}
      {yearsError && <FormHelperText>Could not load tax years</FormHelperText>}
    </FormControl>
  );

  return (
    <React.Fragment>
      <PageHeader
        title="Payments"
        helmetTitle="Payments"
        subtitle="Taxable amounts paid per payee for a tax year, and their 1099s."
        breadcrumbs={[{ label: "Payments" }, { label: "Payments" }]}
        actions={yearSelect}
      />

      <Card>
        <FilterBar>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            label="Search payments"
            placeholder="Search by payee ID, name, address or amount"
          />
          {canDownload && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <LoadingButton
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined />}
                loadingPosition="start"
                onClick={fetchDataAndDownloadCSV}
                loading={loadingButton}
                sx={{ whiteSpace: "nowrap" }}
              >
                Download 1099 summary (CSV)
              </LoadingButton>
            </>
          )}
        </FilterBar>
        {canDownload && (
          <>
            <Divider />
            <Box sx={{ px: 4, py: 3 }}>
              <DetailsExportCluster
                paymentCountFilter={paymentCountFilter}
                onPaymentCountFilterChange={handlePaymentCountFilterChange}
                clientCountFilter={clientCountFilter}
                onClientCountFilterChange={handleClientCountFilterChange}
                onDownload={fetchDataAndDownloadDetailCSV}
                loading={loadingButton}
                buttonLabel="Download payment details (CSV)"
              />
            </Box>
            <Divider />
            <SettingRow
              year={year}
              checked={checked}
              onChange={handleChangePDF}
            />
          </>
        )}
        <Divider />
        <Box sx={{ height: 620, width: "100%" }}>
          <DataGrid
            rows={data}
            loading={loading}
            columns={columns}
            rowCount={totalCount}
            rowHeight={56}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[50, 100, 200]}
            paginationModel={{
              page,
              pageSize: rowsPerPage,
            }}
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            slots={{ noRowsOverlay: NoRowsOverlay }}
            initialState={{
              columns: { columnVisibilityModel: { id: false } },
            }}
            disableRowSelectionOnClick
            // The card draws the border around the grid.
            sx={{ border: 0 }}
          />
        </Box>
      </Card>
    </React.Fragment>
  );
}

export default Payments;
